<template>
    <router-link
        v-if="link"
        ref="linkEl"
        :to="`/tickets/${ticketKey}`"
        class="ticket-key text-data"
    >
        {{ ticketKey }}
    </router-link>
    <span v-else class="ticket-key ticket-key--plain text-data">{{ ticketKey }}</span>
</template>

<script setup lang="ts">
// Renders a ticket key (e.g. PROC-332) in the data/identifier type style so
// keys look the same everywhere: tables, titles, links, comments.
import type { TicketKey } from "@/api/tickets";
import { onBeforeUnmount, useTemplateRef, watch } from "vue";
import type { RouterLink } from "vue-router";
import { bindTicketHover } from "@/composables/ticketHoverCard";

type Props = {
    ticketKey: TicketKey | string;
    link?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
    link: true,
});

const linkEl = useTemplateRef<InstanceType<typeof RouterLink>>("linkEl");
let unbind: (() => void) | undefined;

watch(
    linkEl,
    (instance) => {
        unbind?.();
        unbind = undefined;
        const el = (instance as { $el?: HTMLElement } | null)?.$el;
        if (el instanceof HTMLElement) unbind = bindTicketHover(el, props.ticketKey);
    },
    { immediate: true }
);

onBeforeUnmount(() => unbind?.());
</script>

<style lang="scss">
.ticket-key {
    font-size: 0.9em;
    font-weight: 600;
    color: rgb(var(--v-theme-primary));
    text-decoration: none;

    &:hover {
        text-decoration: underline;
    }

    &--plain {
        color: inherit;
    }
}
</style>
